import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { BellRing, FileText, MessageSquare, Briefcase, ShieldCheck, FileSpreadsheet, Ticket } from 'lucide-react';
import { toast } from 'sonner';

const ADMIN_ALERT_TYPES = [
  { key: 'notify_new_price_request', icon: FileText,
    title: 'Nouvelle demande de prix',
    desc: 'Recevoir une copie quand une nouvelle demande est diffusée aux sous-traitants (1 mail récapitulatif par diffusion).' },
  { key: 'notify_new_message', icon: MessageSquare,
    title: 'Nouveau message à un sous-traitant',
    desc: 'Recevoir une copie de chaque message envoyé à un sous-traitant (audit trail).' },
  { key: 'notify_project_assigned', icon: Briefcase,
    title: 'Projet attribué',
    desc: 'Recevoir une copie quand vous attribuez un projet à un sous-traitant.' },
  { key: 'notify_nda_pending', icon: ShieldCheck,
    title: 'Relance NDA',
    desc: 'Recevoir une copie quand un NDA est envoyé à signer.' },
  { key: 'notify_quote_status', icon: FileSpreadsheet,
    title: 'Statut de devis',
    desc: 'Recevoir une copie quand un devis sous-traitant est accepté/refusé par le client final.' },
  { key: 'notify_ticket_resolved', icon: Ticket,
    title: 'Ticket résolu',
    desc: 'Recevoir une copie quand un ticket sous-traitant est marqué résolu.' },
];

export default function AdminNotificationSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/subcontractors/admin/notification-settings');
        setSettings(data);
      } catch {
        toast.error('Erreur chargement préférences notifications');
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = async (key, value) => {
    setSavingKey(key);
    const previous = settings[key];
    setSettings(s => ({ ...s, [key]: value }));
    try {
      const { data } = await api.put('/subcontractors/admin/notification-settings', { [key]: value });
      setSettings(data);
      toast.success(value ? 'Alerte activée' : 'Alerte désactivée');
    } catch {
      setSettings(s => ({ ...s, [key]: previous }));
      toast.error('Erreur de sauvegarde');
    } finally { setSavingKey(null); }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 sm:p-6" data-testid="admin-notif-card">
      <div className="flex items-center gap-2 mb-1">
        <BellRing className="w-5 h-5 text-amber-400" />
        <h2 className="text-lg font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk' }}>
          Notifications email — Admin
        </h2>
      </div>
      <p className="text-xs text-zinc-500 mb-5">
        Recevez une copie des alertes envoyées aux sous-traitants. Pratique pour garder un œil sur tout ce qui se passe.
      </p>

      {loading ? (
        <div className="text-zinc-500 text-sm py-4">Chargement…</div>
      ) : !settings ? (
        <div className="text-rose-400 text-sm">Impossible de charger.</div>
      ) : (
        <div className="divide-y divide-zinc-800">
          {ADMIN_ALERT_TYPES.map(({ key, icon: Icon, title, desc }) => (
            <div key={key} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0" data-testid={`admin-alert-row-${key}`}>
              <div className="w-8 h-8 rounded-sm bg-amber-500/10 flex items-center justify-center shrink-0">
                <Icon className="w-3.5 h-3.5 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-100">{title}</div>
                <p className="text-[11px] text-zinc-500 mt-0.5">{desc}</p>
              </div>
              <Switch
                checked={!!settings[key]}
                onCheckedChange={(v) => toggle(key, v)}
                disabled={savingKey === key}
                data-testid={`admin-toggle-${key}`}
                className="data-[state=checked]:bg-amber-500 shrink-0"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
